/**
 * Eigentuemerwechsel — die Maske dazu.
 *
 * `TransferOwnershipDialog` liegt seit jeher in `core-frontend` und wurde von
 * **keiner** App gehostet (gemessen 2026-08-20 ueber alle vier App-Frontends:
 * `verein` uebersetzt lediglich einen seiner Strings, gerendert hat ihn
 * niemand). Der Eigentuemerwechsel war damit nur per tRPC-Aufruf erreichbar —
 * gebaut, aber nicht erreichbar. Diese Sektion haengt ihn an die
 * Mitglieder-Seite.
 *
 * Gehostet wird **beides**, Dialog und Banner, und das ist kein Beiwerk: der
 * Dialog beantwortet einen laufenden Wechsel mit „Es laeuft bereits eine
 * Uebertragung — bitte abbrechen oder warten". Das Abbrechen lebt im
 * `PendingOwnershipTransferBanner`. Ohne ihn zeigt die Fehlermeldung auf eine
 * Flaeche, die es nicht gibt.
 */

import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import {
  type Member,
  type MemberListFilters,
  PendingOwnershipTransferBanner,
  TransferOwnershipDialog,
  useMembers,
  usePendingOwnershipTransfer,
} from '@xhubio-saas/core-frontend/plugin-user'
import { Button } from '@xhubio-saas/core-frontend/ui-components'
import { Crown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserClient } from '../api/userClient'
import { useIsOrgOwner } from '../hooks/useMemberAdminRights'

/**
 * Wer kommt als neuer Eigentuemer in Frage?
 *
 * 🔴 `status === 'invited'` fliegt raus, weil diese Zeilen **keine Nutzer**
 * sind: `members.list` mischt offene Einladungen als Pseudo-Zeilen mit der Id
 * `invitation-<id>` unter die Mitglieder. Der Server verlangt fuer den
 * Empfaenger eine echte `member`-Zeile und antwortet sonst mit „Recipient must
 * be a member of the organization — invite them first". Ein solcher Eintrag im
 * Auswahlfeld waere eine garantierte Sackgasse.
 *
 * ⚪ `inactive` bleibt drin: der Server laesst es zu (er prueft nur die
 * Mitgliedschaft), und stiller strenger sein als der Server heisst, eine
 * Moeglichkeit zu verstecken, die es gibt.
 *
 * 🔵 Sich selbst kann man die Organisation nicht uebergeben — der Server weist
 * es mit „You cannot transfer ownership to yourself" ab.
 */
export function eigentumsKandidaten(
  mitglieder: Member[],
  eigeneUserId: string | undefined
): Member[] {
  return mitglieder.filter((m) => m.status !== 'invited' && m.userId !== eigeneUserId)
}

/**
 * 🔴 `pageSize: 100` ist die **Obergrenze des Servers** (`ListMembersSchema`:
 * `limit: z.number().min(1).max(100)`), nicht eine gegriffene Zahl. Eine
 * Organisation mit mehr als 100 Mitgliedern saehe im Auswahlfeld nur die
 * ersten 100 — das ist eine bekannte Grenze, kein Versehen.
 */
const KANDIDATEN_FILTER: MemberListFilters = { page: 1, pageSize: 100 }

/**
 * Der Teil, der Daten holt. Bewusst eine eigene Komponente: die Hooks duerfen
 * nicht hinter einer Bedingung stehen, und ein Nicht-Eigentuemer soll die
 * Mitgliederliste nicht ein zweites Mal laden.
 */
function EigentuemerwechselFuerOwner() {
  const { t } = useTranslation('user')
  const apiClient = useUserClient()
  const { user } = useAuth()
  const [istOffen, setIstOffen] = useState(false)

  const { data, refetch: mitgliederNeuLaden } = useMembers(apiClient, KANDIDATEN_FILTER)
  const { pending, refetch: uebertragungNeuLaden } = usePendingOwnershipTransfer(apiClient)

  const kandidaten = useMemo(
    () => eigentumsKandidaten(data.items, user?.id),
    [data.items, user?.id]
  )

  return (
    <div className="container mx-auto px-4 pb-8 space-y-4" data-testid="ownership-transfer-section">
      {pending && (
        <PendingOwnershipTransferBanner
          apiClient={apiClient}
          pending={pending}
          canCancel
          onCancelled={() => {
            void uebertragungNeuLaden()
          }}
          t={t}
        />
      )}

      <div className="flex flex-col gap-3 rounded-xl border border-[var(--border)] p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Crown className="mt-0.5 h-5 w-5 flex-shrink-0 text-[var(--muted-foreground)]" />
          <div>
            <h2 className="font-medium">{t('transferOwnership.title')}</h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              {t('transferOwnership.description')}
            </p>
          </div>
        </div>
        {/* Primaeraktion rechts (UI-Guidelines). Waehrend eine Uebertragung
            laeuft, ist der Knopf aus: der Server wuerde mit CONFLICT antworten,
            der Weg dahin fuehrt ueber das Abbrechen im Banner darueber. */}
        <div className="flex justify-end">
          <Button
            data-testid="users-btn-transfer-ownership"
            onClick={() => setIstOffen(true)}
            disabled={!!pending}
          >
            {t('transferOwnership.submit')}
          </Button>
        </div>
      </div>

      <TransferOwnershipDialog
        apiClient={apiClient}
        candidates={kandidaten}
        open={istOffen}
        onOpenChange={setIstOffen}
        onSuccess={() => {
          void uebertragungNeuLaden()
          void mitgliederNeuLaden()
        }}
        t={t}
      />
    </div>
  )
}

/**
 * Sichtbar **nur fuer den Eigentuemer** — dieselbe Grenze, die der Server
 * zieht. Solange die Rolle unbekannt ist, ist sie unsichtbar: „weiss nicht"
 * heisst hier „nein", der Knopf endet in einer Mutation.
 */
export function OwnershipTransferSection() {
  const istEigentuemer = useIsOrgOwner()
  if (!istEigentuemer) return null
  return <EigentuemerwechselFuerOwner />
}
